import {
  createInfiniteSuspenseQuery,
  createMutation,
  createSuspenseQuery,
  makeQueryKey,
} from "@steto-care/steto-query";

import { MessagingRepository } from "@/io/repository/MessagingRepository.ts";
import { CreateMessage } from "@/models/MessagingModel.ts";
import { PractitionerQueries } from "@/queries/PractitionerQueries.ts";

type MessagesRequest = {
  patientId: string;
  cursor?: string;
};

export class MessagingQueries {
  constructor(
    private readonly practitioners: PractitionerQueries = new PractitionerQueries(),
    private readonly messaging: MessagingRepository = new MessagingRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useMessages = createInfiniteSuspenseQuery(
    "chat",
    ({ patientId }) => makeQueryKey(patientId),
    ({ patientId, cursor }: MessagesRequest) => this.messaging.getMessages(patientId, cursor),
    (lastPage, _, lastPageParam) => {
      if (!lastPage.next) {
        return null;
      }
      const url = new URL(lastPage.next);
      const cursor = url.searchParams.get("cursor") ?? undefined;
      if (!cursor) {
        return null;
      }
      return {
        ...lastPageParam,
        cursor,
      };
    },
    {
      getPreviousPageParam: (firstPage, _, firstPageParam) => {
        if (!firstPage.prev) {
          return null;
        }
        const url = new URL(firstPage.prev);
        const cursor = url.searchParams.get("cursor") ?? undefined;
        if (!cursor) {
          return null;
        }
        return {
          ...firstPageParam,
          cursor,
        };
      },
    },
  );

  useDraftMessage = createSuspenseQuery(
    "use-draft-message",
    ({ practitionerId, patientId }) => makeQueryKey(practitionerId, patientId),
    async (data: { practitionerId: string; patientId: string }) => ({
      message: await this.messaging.getDraftMessage(data.practitionerId, data.patientId),
      patientId: data.patientId,
    }),
  );

  useUnreadMessages = createSuspenseQuery(
    "unread-messages",
    ({ patientId }) => makeQueryKey(patientId),
    async (data: { patientId: string }) => this.messaging.getUnreadMessages(data.patientId),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  /*
  Don't invalidate messages on read.
  Keep unread messages display until the next natural refresh
   */
  useReadMessages = createMutation(
    "read-messages",
    async ({ patientId }: { patientId: string }) => this.messaging.readMessages(patientId),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useUnreadMessages.invalidate(client, { patientId });
        this.practitioners.useDoctorPatientList.invalidate(client, {});
        this.practitioners.useNursePatientList.invalidate(client, {});
      },
    },
  );

  useCreateMessage = createMutation(
    "create-message",
    async ({ patientId, ...data }: { patientId: string } & CreateMessage) =>
      this.messaging.createMessage(patientId, data),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useMessages.invalidate(client, {
          patientId,
        });
      },
    },
  );

  useBroadcastMessage = createMutation("broadcast-message", async ({ contentText }: { contentText: string }) =>
    this.messaging.broadcastMessage(contentText),
  );

  useDeleteMessage = createMutation(
    "delete-message",
    async ({ patientId, messageId }: { patientId: string; messageId: string }) =>
      this.messaging.deleteMessage(patientId, messageId),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useMessages.invalidate(client, {
          patientId,
        });
      },
    },
  );

  /*
  WhatsApp semantics: clicking the emoji you already reacted with removes it (DELETE);
  any other emoji sets/replaces your single reaction (POST). The server upserts on (message, user).
   */
  useToggleReaction = createMutation(
    "toggle-reaction",
    async ({
      patientId,
      messageId,
      emoji,
      currentEmoji,
    }: {
      patientId: string;
      messageId: string;
      emoji: string;
      currentEmoji?: string;
    }) =>
      currentEmoji === emoji
        ? this.messaging.removeReaction(patientId, messageId)
        : this.messaging.setReaction(patientId, messageId, emoji),
    {
      onSuccess: (_, { patientId }, { client }) => {
        this.useMessages.invalidate(client, {
          patientId,
        });
      },
    },
  );

  useUpdateDraftMessage = createMutation(
    "update-draft-message",
    async ({ practitionerId, patientId, message }: { practitionerId: string; patientId: string; message?: string }) =>
      message
        ? this.messaging.draftMessage(practitionerId, patientId, message)
        : this.messaging.clearDraftMessage(practitionerId, patientId),
    {
      onSuccess: (_, { practitionerId, patientId }, { client }) => {
        this.useDraftMessage.invalidate(client, {
          practitionerId,
          patientId,
        });
      },
    },
  );
}
